// Recommendation API service for AI-powered golf equipment recommendations

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper: get access token from whichever storage is active
function getToken(): string | null {
  const storageType = localStorage.getItem('auth_storage_type');
  const storage = storageType === 'session' ? sessionStorage : localStorage;
  return storage.getItem('access_token');
}

// Add token to all requests
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// On 401, signal the auth context to show the session-expired overlay
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
    }
    return Promise.reject(error);
  }
);

// Types for recommendation system
export interface UserCriteria {
  equipment_type: 'woods' | 'irons' | 'wedges';
  handicap?: number;
  height_inches?: number;
  weight_lbs?: number;
  age?: number;
  swing_speed_mph?: number;
  typical_distance?: number;
  playing_style?: 'aggressive' | 'conservative' | 'balanced';
  primary_goal?: 'distance' | 'accuracy' | 'consistency' | 'feel';
  forgiveness_preference?: 'high' | 'medium' | 'low';
  preferred_brands?: string[];
  budget_min?: number;
  budget_max?: number;
  session_id?: string;
  // New fields for BasicRecommendationForm
  gender?: string;
  shot_shape?: string;
  preferred_ball_flight?: string;
  rounds_per_month?: number;
  years_playing?: number;
}

export interface ShaftOption {
  shaft_brand: string;
  shaft_model: string;
  flex: string;
  weight: number;
  shaft_score?: number;
}

export interface EquipmentRecommendation {
  equipment_id: number;
  equipment_type: string;
  manufacturer: string;
  model: string;
  loft?: number;
  price?: number;
  overall_score: number;
  confidence_score: number;
  rank_position: number;
  distance_rating?: number;
  forgiveness_rating?: number;
  workability_rating?: number;
  feel_rating?: number;
  fit_reason: string;
  detailed_explanation?: string;
  key_features?: string[];
  technology_highlights?: string[];
  top_shafts?: ShaftOption[];
  stock_shaft_recommendation?: string;
}

export interface RecommendationResponse {
  request_id: string;
  equipment_type: string;
  criteria_summary: string;
  recommendations: EquipmentRecommendation[];
  total_considered: number;
  related_questions?: string[];
  processing_time_ms: number;
  confidence_level: 'high' | 'medium' | 'low';
}

// API calls
export const getRecommendations = async (criteria: UserCriteria): Promise<RecommendationResponse> => {
  console.log('🚀 Making recommendation request with criteria:', criteria);
  console.log('🌐 API Base URL:', API_BASE_URL);
  
  try {
    // Convert criteria to query parameters for the components/recommendations endpoint
    const params = new URLSearchParams();
    
    // Map equipment type to club type
    const clubTypeMap: Record<string, string> = {
      'woods': 'Driver',
      'irons': 'Iron',
      'wedges': 'Wedge'
    };
    params.append('club_type', clubTypeMap[criteria.equipment_type] || 'Driver');

    // Add all player profile parameters for rule-based scoring
    if (criteria.handicap != null) params.append('handicap', criteria.handicap.toString());
    if (criteria.swing_speed_mph != null) params.append('swing_speed', criteria.swing_speed_mph.toString());
    if (criteria.age != null) params.append('age', criteria.age.toString());
    if (criteria.gender) params.append('gender', criteria.gender);
    if (criteria.years_playing != null) params.append('years_playing', criteria.years_playing.toString());
    if (criteria.budget_max != null) params.append('budget_max', criteria.budget_max.toString());

    // Send primary_goal directly (new rule-based scoring uses this)
    if (criteria.primary_goal) params.append('primary_goal', criteria.primary_goal);

    params.append('limit', '10');
    
    const fullUrl = `/components/recommendations/?${params.toString()}`;
    console.log('🔗 Full API URL:', `${API_BASE_URL}${fullUrl}`);
    console.log('📝 Query params:', params.toString());
    
    const response = await api.get(fullUrl);
    console.log('✅ Recommendation response received:', response.data);
    console.log('📊 Response length:', response.data.length);
    
    // Transform the response to match our RecommendationResponse interface
    const transformedResponse: RecommendationResponse = {
      request_id: criteria.session_id || 'basic-request',
      equipment_type: criteria.equipment_type,
      criteria_summary: formatCriteriaForDisplay(criteria),
      recommendations: response.data.map((item: any, index: number) => ({
        equipment_id: item.head?.id || index,
        equipment_type: criteria.equipment_type,
        manufacturer: item.head?.manufacturer || 'Unknown',
        model: item.head?.model || 'Unknown',
        loft: item.head?.loft,
        price: item.head?.msrp || item.price,
        overall_score: item.match_score || 0,
        confidence_score: 0.9,
        rank_position: index + 1,
        distance_rating: item.score_breakdown?.goal_score,
        forgiveness_rating: item.score_breakdown?.moi_score,
        workability_rating: item.score_breakdown?.weight_score,
        feel_rating: item.score_breakdown?.flex_score,
        fit_reason: `${item.fit_quality || 'Good'} match (${(item.match_score || 0).toFixed(1)}/10)`,
        detailed_explanation: item.recommendation_reason || 'Good fit for your swing profile.',
        key_features: [
          `Score: ${(item.match_score || 0).toFixed(1)}/10`,
          `Flex match: ${(item.score_breakdown?.flex_score || 0).toFixed(1)}/10`,
          `Goal fit: ${(item.score_breakdown?.goal_score || 0).toFixed(1)}/10`,
          `Forgiveness: ${(item.score_breakdown?.moi_score || 0).toFixed(1)}/10`,
        ].filter(Boolean),
        technology_highlights: [
          item.recommendation_reason
        ].filter(Boolean),
        top_shafts: item.top_shafts || [],
        stock_shaft_recommendation: item.stock_shaft_recommendation || '',
      })),
      total_considered: response.data.length,
      related_questions: [
        'Would you like to see alternative shaft options?',
        'Are you interested in custom fitting services?',
        'Would you like recommendations for other club types?'
      ],
      processing_time_ms: 150,
      confidence_level: response.data.length > 5 ? 'high' : response.data.length > 2 ? 'medium' : 'low'
    };
    
    return transformedResponse;
  } catch (error: any) {
    console.error('❌ Recommendation API error:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get recommendations');
  }
};

export const getRecommendationRequest = async (requestId: string) => {
  const response = await api.get(`/recommendations/requests/${requestId}`);
  return response.data;
};

// Utility functions
export const generateSessionId = (): string => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

export const formatCriteriaForDisplay = (criteria: UserCriteria): string => {
  const parts = [];
  
  if (criteria.handicap) {
    parts.push(`${criteria.handicap} handicap`);
  }
  
  if (criteria.swing_speed_mph) {
    parts.push(`${criteria.swing_speed_mph} mph swing speed`);
  }
  
  if (criteria.budget_max) {
    parts.push(`under $${criteria.budget_max.toFixed(0)}`);
  }
  
  if (criteria.primary_goal) {
    parts.push(`focused on ${criteria.primary_goal}`);
  }
  
  if (criteria.preferred_brands && criteria.preferred_brands.length > 0) {
    parts.push(`preferring ${criteria.preferred_brands.join(', ')}`);
  }
  
  return parts.length > 0 ? parts.join(', ') : 'general search';
};

// Advanced Profile API calls
export interface AdvancedProfileResponse {
  profile_id: number;
  profile_created: boolean;
  fitting_summary: any;
  next_step: string;
}

export const createAdvancedProfile = async (profileData: any): Promise<AdvancedProfileResponse> => {
  try {
    console.log('🚀 Creating advanced profile:', profileData);
    const response = await api.post('/advanced/profile/', profileData);
    console.log('✅ Advanced profile created:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Advanced profile creation error:', error);
    console.error('Error response:', error.response?.data);

    // Handle FastAPI validation errors (422)
    if (error.response?.status === 422 && error.response?.data?.detail) {
      const detail = error.response.data.detail;

      // If detail is an array of validation errors
      if (Array.isArray(detail)) {
        const errors = detail.map((err: any) =>
          `${err.loc?.join('.') || 'Field'}: ${err.msg}`
        ).join('; ');
        throw new Error(`Validation error: ${errors}`);
      }

      // If detail is a string
      if (typeof detail === 'string') {
        throw new Error(detail);
      }

      // If detail is an object, stringify it
      throw new Error(`Validation error: ${JSON.stringify(detail)}`);
    }

    throw new Error(error.response?.data?.detail || error.message || 'Failed to create advanced profile');
  }
};

export const getProfileById = async (profileId: number) => {
  try {
    const response = await api.get(`/advanced/profile/${profileId}`);
    return response.data;
  } catch (error: any) {
    console.error('❌ Get profile by ID error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get profile');
  }
};

export const getAdvancedProfile = async (sessionId: string) => {
  try {
    const response = await api.get(`/advanced/profile/${sessionId}`);
    return response.data;
  } catch (error: any) {
    console.error('❌ Get advanced profile error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get advanced profile');
  }
};

export const getAdvancedRecommendations = async (sessionId: string, clubType: string, limit: number = 5) => {
  try {
    const response = await api.get(`/advanced/recommendations/${sessionId}?club_type=${clubType}&limit=${limit}`);
    return response.data;
  } catch (error: any) {
    console.error('❌ Get advanced recommendations error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get advanced recommendations');
  }
};

// ============================================
// USER PROFILE MANAGEMENT APIs (NEW)
// ============================================

export interface UserProfileSummary {
  profile_id: number;
  session_id: string;
  created_at: string;
  handicap: number | null;
  swing_speed_driver: number | null;
  is_synthetic: boolean;
  fitting_summary: any;
}

export interface ProfilePrefillResponse {
  prefill_data: any;
  source_profile_id: number;
  source_created_at: string;
  total_previous_fittings: number;
  fields_filled: number;
  strategy_used: string;
  message: string;
}

/**
 * Get all profiles for the current user
 */
export const getMyProfiles = async (skip: number = 0, limit: number = 100): Promise<UserProfileSummary[]> => {
  try {
    const response = await api.get(`/advanced/profile/me/all?skip=${skip}&limit=${limit}`);
    return response.data;
  } catch (error: any) {
    console.error('❌ Get my profiles error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get profile history');
  }
};

/**
 * Get the latest profile for the current user
 */
export const getMyLatestProfile = async (): Promise<any> => {
  try {
    const response = await api.get('/advanced/profile/me/latest');
    return response.data;
  } catch (error: any) {
    console.error('❌ Get latest profile error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get latest profile');
  }
};

/**
 * Get the total count of user's profiles
 */
export const getMyProfileCount = async (): Promise<{ user_id: number; total_fittings: number; email: string }> => {
  try {
    const response = await api.get('/advanced/profile/me/count');
    return response.data;
  } catch (error: any) {
    console.error('❌ Get profile count error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get profile count');
  }
};

/**
 * Get profile data to pre-fill a new fitting form
 * @param profileId - Optional specific profile ID to use
 * @param strategy - Selection strategy: 'latest' (most recent) or 'most_complete' (most fields filled)
 */
export const getPrefillData = async (profileId?: number, strategy: 'latest' | 'most_complete' = 'latest'): Promise<ProfilePrefillResponse> => {
  try {
    let url = '/advanced/profile/me/prefill';
    const params = new URLSearchParams();

    if (profileId) {
      params.append('profile_id', profileId.toString());
    } else {
      params.append('strategy', strategy);
    }

    if (params.toString()) {
      url += '?' + params.toString();
    }

    const response = await api.get(url);
    return response.data;
  } catch (error: any) {
    console.error('❌ Get prefill data error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get prefill data');
  }
};

// ML Recommendations Types
export interface MLShaftRecommendation {
  shaft_id: number;
  shaft_brand: string;
  shaft_model: string;
  shaft_flex: string;
  shaft_weight: number;
  predicted_rating?: number;
  // 3-stage ML scores
  head_score?: number;
  shaft_score?: number;
  combined_score?: number;
  // Rule-based extras (V5.1)
  base_combined?: number;
  penalty_multiplier?: number;
  penalty_reasons?: string;
  predicted_spin?: number;
  easy_to_hit_score?: number;
  // Detailed breakdown
  moi_score?: number;
  tech_score?: number;
  loft_score?: number;
  flex_score?: number;
  weight_score?: number;
  spin_optimization_score?: number;
  player_fit_score?: number;
  synergy_score?: number;
  launch_complement_score?: number;
  stability_fit_score?: number;
  score_reasons?: Array<{
    key: string;
    score: number;
    label: string;
    detail: string;
    flag: 'good' | 'ok' | 'warn';
  }>;
  combo_radar?: {
    head_radar: Record<string, number>;
    shaft_radar: Record<string, number>;
  };
}

export interface MLStockShaft {
  shaft_id: number;
  shaft_brand: string;
  shaft_model: string;
  flex: string;
  weight: number | null;
  fit_tier: 'stock_standard' | 'stock_alternate';
  upcharge_usd: number | null;
  description: string;
}

export interface MLHeadRecommendation {
  head_id: number;
  head_brand: string;
  head_model: string;
  head_loft: number;
  head_score: number;
  shafts: MLShaftRecommendation[];
  stock_shaft: MLStockShaft | null;
}

export interface MLRecommendationsResponse {
  heads: MLHeadRecommendation[];
  metadata: {
    profile_id: number;
    total_heads: number;
    shafts_per_head: number;
    filtering_mode?: string;
    scoring_mode?: 'ml' | 'rule_based';
    scoring_version?: string;
    total_combos_evaluated?: number;
  };
  status: string;
  equipment_type: string;
}

/**
 * Get ML-powered structured driver recommendations
 * Returns top 20 heads with top 10 shafts each + stock shaft calculation
 */
export const getMLDriverRecommendations = async (profileId: number): Promise<MLRecommendationsResponse> => {
  try {
    console.log(`🎯 Fetching ML recommendations for profile ${profileId}`);
    const response = await api.post(`/ml/recommendations/drivers/structured?profile_id=${profileId}`);
    console.log('✅ ML recommendations received:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Get ML recommendations error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get ML recommendations');
  }
};

/**
 * Get ML-powered 3-stage driver recommendations
 * Returns top 20 heads with interpretable scores (head, shaft, combined)
 */
export const getMLDriverRecommendations3Stage = async (profileId: number): Promise<MLRecommendationsResponse> => {
  try {
    console.log(`🎯 Fetching 3-stage ML recommendations for profile ${profileId}`);
    const response = await api.post(`/ml/recommendations/drivers/3stage?profile_id=${profileId}`);
    console.log('✅ 3-stage ML recommendations received:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Get 3-stage ML recommendations error:', error);
    console.error('Error response:', error.response?.data);

    // Handle structured error responses (prevent "[object Object]")
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;

      // If detail is an array of validation errors
      if (Array.isArray(detail)) {
        const errors = detail.map((err: any) =>
          `${err.loc?.join('.') || 'Field'}: ${err.msg}`
        ).join('; ');
        throw new Error(`Validation error: ${errors}`);
      }

      // If detail is an object (not string), stringify it
      if (typeof detail === 'object' && detail !== null) {
        throw new Error(`Error: ${JSON.stringify(detail)}`);
      }

      // If detail is a string, use it directly
      if (typeof detail === 'string') {
        throw new Error(detail);
      }
    }

    throw new Error(error.message || 'Failed to get 3-stage ML recommendations');
  }
};

/**
 * Get ML-powered 3-stage iron recommendations
 * Returns top 20 heads with interpretable scores (head, shaft, combined)
 */
export const getMLIronRecommendations3Stage = async (profileId: number): Promise<MLRecommendationsResponse> => {
  try {
    console.log(`🎯 Fetching 3-stage ML iron recommendations for profile ${profileId}`);
    const response = await api.post(`/ml/recommendations/irons/3stage?profile_id=${profileId}`);
    console.log('✅ 3-stage ML iron recommendations received:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Get 3-stage ML iron recommendations error:', error);
    console.error('Error response:', error.response?.data);

    // Handle structured error responses (prevent "[object Object]")
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;

      // If detail is an array of validation errors
      if (Array.isArray(detail)) {
        const errors = detail.map((err: any) =>
          `${err.loc?.join('.') || 'Field'}: ${err.msg}`
        ).join('; ');
        throw new Error(`Validation error: ${errors}`);
      }

      // If detail is an object (not string), stringify it
      if (typeof detail === 'object' && detail !== null) {
        throw new Error(`Error: ${JSON.stringify(detail)}`);
      }

      // If detail is a string, use it directly
      if (typeof detail === 'string') {
        throw new Error(detail);
      }
    }

    throw new Error(error.message || 'Failed to get 3-stage ML iron recommendations');
  }
};

/**
 * Get rule-based (V5.1) driver recommendations
 * Uses deterministic formulas instead of ML model
 * Returns same format as ML endpoint but with detailed score breakdown
 */
export const getIronRecommendationsRuleBased = async (profileId: number): Promise<any> => {
  try {
    console.log(`📐 Fetching rule-based iron recommendations for profile ${profileId}`);
    const response = await api.post(`/ml/recommendations/irons/rule-based?profile_id=${profileId}`);
    console.log('✅ Rule-based iron recommendations received:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Get rule-based iron recommendations error:', error);
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === 'string') throw new Error(detail);
    }
    throw new Error(error.message || 'Failed to get rule-based iron recommendations');
  }
};

export const getWedgeRecommendationsRuleBased = async (profileId: number): Promise<any> => {
  try {
    console.log(`📐 Fetching rule-based wedge recommendations for profile ${profileId}`);
    const response = await api.post(`/ml/recommendations/wedges/rule-based?profile_id=${profileId}`);
    console.log('✅ Rule-based wedge recommendations received:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Get rule-based wedge recommendations error:', error);
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === 'string') throw new Error(detail);
    }
    throw new Error(error.message || 'Failed to get rule-based wedge recommendations');
  }
};

export const getDriverRecommendationsRuleBased = async (profileId: number): Promise<MLRecommendationsResponse> => {
  try {
    console.log(`📐 Fetching rule-based recommendations for profile ${profileId}`);
    const response = await api.post(`/ml/recommendations/drivers/rule-based?profile_id=${profileId}`);
    console.log('✅ Rule-based recommendations received:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Get rule-based recommendations error:', error);
    console.error('Error response:', error.response?.data);

    // Handle structured error responses
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;

      if (Array.isArray(detail)) {
        const errors = detail.map((err: any) =>
          `${err.loc?.join('.') || 'Field'}: ${err.msg}`
        ).join('; ');
        throw new Error(`Validation error: ${errors}`);
      }

      if (typeof detail === 'object' && detail !== null) {
        throw new Error(`Error: ${JSON.stringify(detail)}`);
      }

      if (typeof detail === 'string') {
        throw new Error(detail);
      }
    }

    throw new Error(error.message || 'Failed to get rule-based recommendations');
  }
};

// ---------------------------------------------------------------------------
// V9 Types + API
// ---------------------------------------------------------------------------

export interface PlayerNeed {
  category: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  direction: 'INCREASE' | 'REDUCE' | 'NONE';
  delta: number;
  confidence: number;
  human_label: string;
  equipment_hint: string;
}

export interface PlayerNeeds {
  needs: PlayerNeed[];
  profile_completeness: number;
}

export interface PlayerArchetype {
  label: string;
  strike_quality: number;
  face_control: number;
  spin_consistency: number;
  distance_consistency: number;
  accuracy: number;
  confidence: number;
  data_source: string;
}

export interface ApproachCombo {
  head: {
    head_brand: string;
    head_model: string;
    head_loft: number;
    head_score: number;
  };
  shaft: {
    shaft_brand: string;
    shaft_model: string;
    shaft_flex: string;
    shaft_weight: number;
    shaft_score: number;
  };
  combined_score: number;
}

export interface ApproachPath {
  label: string;
  rationale: string;
  archetype_key: string;
  primary_need: string;
  top_combos: ApproachCombo[];
}

export const getDriverRecommendationsV9 = async (profileId: number): Promise<any> => {
  try {
    console.log(`🚀 Fetching V9 driver recommendations for profile ${profileId}`);
    const response = await api.post(`/ml/recommendations/drivers/v9?profile_id=${profileId}`);
    console.log('✅ V9 driver recommendations received:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ V9 driver recommendations error:', error);
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === 'string') throw new Error(detail);
    }
    throw new Error(error.message || 'Failed to get V9 driver recommendations');
  }
};

export const getIronRecommendationsV9 = async (profileId: number): Promise<any> => {
  try {
    console.log(`🚀 Fetching V9 iron recommendations for profile ${profileId}`);
    const response = await api.post(`/ml/recommendations/irons/v9?profile_id=${profileId}`);
    console.log('✅ V9 iron recommendations received:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ V9 iron recommendations error:', error);
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === 'string') throw new Error(detail);
    }
    throw new Error(error.message || 'Failed to get V9 iron recommendations');
  }
};

// ---------------------------------------------------------------------------
// Radar Profile Types + API
// ---------------------------------------------------------------------------

export interface RadarAxisValue {
  key: string;
  label: string;
  value: number;
  description: string;
}

export interface RadarProfileResponse {
  head_radar: RadarAxisValue[];
  shaft_radar: RadarAxisValue[];
}

export const getRadarProfile = async (
  profileId: number,
  profileSource: string = 'advanced'
): Promise<RadarProfileResponse> => {
  const response = await api.get('/ml/recommendations/radar-profile', {
    params: { profile_id: profileId, profile_source: profileSource },
  });
  return response.data;
};

export const radarArrayToRecord = (axes: RadarAxisValue[]): Record<string, number> =>
  Object.fromEntries(axes.map(a => [a.key, a.value]));

export const saveRadarPreferences = async (
  profileId: number,
  prefs: { head: Record<string, number>; shaft: Record<string, number> },
  equipmentType: string = 'driver'
): Promise<void> => {
  await api.put(`/ml/recommendations/radar-preferences/${profileId}?equipment_type=${equipmentType}`, prefs);
};

export const loadRadarPreferences = async (
  profileId: number,
  equipmentType: string = 'driver'
): Promise<{ head: Record<string, number>; shaft: Record<string, number> }> => {
  const response = await api.get(`/ml/recommendations/radar-preferences/${profileId}?equipment_type=${equipmentType}`);
  return response.data;
};
